import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Eye, EyeOff, Lock, Mail, Wrench, ArrowLeft, ShieldCheck } from 'lucide-react';

const GY = { green: '#009b3a', yellow: '#fedd00', red: '#ef3340' };

const GuyarepairLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const { login, verify2FA, isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const otpRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'super_admin') navigate('/super-admin');
      else navigate('/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  // Animated grid background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    let t = 0;
    const draw = () => {
      t += 0.005;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const step = 60;
      ctx.strokeStyle = 'rgba(0, 155, 58, 0.04)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < canvas.width; x += step) {
        const offset = Math.sin(t + x * 0.01) * 8;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + offset, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += step) {
        const offset = Math.cos(t + y * 0.01) * 8;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y + offset);
        ctx.stroke();
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password, false);
    if (result.success) {
      if (result.user.role === 'super_admin') navigate('/super-admin');
      else navigate('/dashboard');
    } else if (result.requires_2fa) {
      setNeeds2FA(true);
      setTempToken(result.temp_token);
      setTimeout(() => otpRef.current?.focus(), 100);
    } else {
      setError(result.error || t('guyarepairLogin.errInvalidCredentials'));
    }
    setLoading(false);
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verify2FA(tempToken, otpCode);
    if (result.success) {
      if (result.user.role === 'super_admin') navigate('/super-admin');
      else navigate('/dashboard');
    } else {
      setError(result.error || t('guyarepairLogin.err2FAInvalid'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: '#050a14' }}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      <div className="relative z-10 w-full max-w-[420px] px-4">
        {/* Brand */}
        <div className="text-center mb-8">
          <img src="/guyarepair_logo.png" alt="GUYAREPAIR" className="w-16 h-16 mx-auto mb-4 object-contain" />
          <h1 className="text-2xl font-black tracking-tight text-white">
            GUYA<span style={{ color: GY.green }}>RE</span><span style={{ color: GY.yellow }}>PA</span><span style={{ color: GY.red }}>IR</span>
          </h1>
          <p className="text-xs text-gray-500 mt-1 tracking-widest uppercase">{t('guyarepairLogin.subscriberSpace')}</p>
        </div>

        {/* Form card */}
        <div className="rounded-2xl p-6 sm:p-8" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          {!needs2FA ? (
            <>
              <h2 className="text-base font-bold text-white mb-1" data-testid="subscriber-login-title">{t('guyarepairLogin.businessLoginTitle')}</h2>
              <p className="text-xs text-gray-500 mb-6">{t('guyarepairLogin.businessLoginSubtitle')}</p>

              {error && (
                <div className="mb-5 px-4 py-3 rounded-lg text-xs text-center font-medium" data-testid="subscriber-login-error"
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('guyarepairLogin.emailLabel')}</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="subscriber-email"
                      className="w-full pl-11 pr-4 py-3.5 rounded-lg text-sm text-white placeholder-gray-600 transition-all duration-200 focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                      onFocus={(e) => { e.target.style.borderColor = `${GY.green}40`; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                      placeholder={t('guyarepairLogin.emailPlaceholder')}
                      required
                      autoFocus
                    />
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('guyarepairLogin.passwordLabel')}</label>
                  <div className="relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="subscriber-password"
                      className="w-full pl-11 pr-12 py-3.5 rounded-lg text-sm text-white placeholder-gray-600 transition-all duration-200 focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                      onFocus={(e) => { e.target.style.borderColor = `${GY.green}40`; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                      placeholder={t('guyarepairLogin.passwordPlaceholder')}
                      required
                    />
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-600">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="subscriber-login-submit"
                  className="w-full py-3.5 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-40 active:scale-[0.98] flex items-center justify-center gap-2 text-[#050a14]"
                  style={{ background: `linear-gradient(135deg, ${GY.green}, ${GY.yellow})`, boxShadow: `0 4px 20px ${GY.green}30` }}
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> {t('guyarepairLogin.loggingIn')}</>
                  ) : (
                    <><Lock className="w-4 h-4" /> {t('guyarepairLogin.loginButton')}</>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${GY.green}20` }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: GY.green }} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white" data-testid="2fa-title">{t('guyarepairLogin.twoFATitle')}</h2>
                  <p className="text-xs text-gray-500">{t('guyarepairLogin.twoFASubtitle')}</p>
                </div>
              </div>

              {error && (
                <div className="mb-5 px-4 py-3 rounded-lg text-xs text-center font-medium" data-testid="2fa-error"
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handle2FAVerify} className="space-y-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('guyarepairLogin.otpLabel')}</label>
                  <input
                    ref={otpRef}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    data-testid="2fa-code-input"
                    className="w-full px-4 py-4 rounded-lg text-center text-2xl tracking-[0.5em] text-white font-mono placeholder-gray-600 transition-all duration-200 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    onFocus={(e) => { e.target.style.borderColor = `${GY.green}40`; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otpCode.length !== 6}
                  data-testid="2fa-verify-submit"
                  className="w-full py-3.5 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-40 active:scale-[0.98] flex items-center justify-center gap-2 text-[#050a14]"
                  style={{ background: `linear-gradient(135deg, ${GY.green}, ${GY.yellow})`, boxShadow: `0 4px 20px ${GY.green}30` }}
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> {t('guyarepairLogin.verifying')}</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4" /> {t('guyarepairLogin.verifyButton')}</>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => { setNeeds2FA(false); setTempToken(''); setOtpCode(''); setError(''); }}
                  data-testid="2fa-back-btn"
                  className="w-full py-2.5 text-xs text-gray-500 hover:text-white transition-colors"
                >
                  {t('guyarepairLogin.backToLogin')}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Links */}
        <div className="mt-6 text-center space-y-3">
          <p className="text-xs text-gray-500">
            {t('guyarepairLogin.notSubscribedYet')}{' '}
            <Link to="/" className="font-semibold transition-colors" style={{ color: GY.green }}>{t('guyarepairLogin.freeTrial')}</Link>
          </p>
        </div>

        <div className="mt-6 flex items-center justify-between text-[10px] text-gray-600">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" style={{ color: GY.green }} />
            <span>{t('guyarepairLogin.secureConnection')}</span>
          </div>
          <Link to="/" className="flex items-center gap-1 transition-colors hover:text-gray-400" data-testid="back-to-guyarepair">
            <ArrowLeft className="w-3 h-3" /> {t('guyarepairLogin.guyarepairHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GuyarepairLoginPage;
